/**
 * SurveyPage - Citizen Survey Generator
 *
 * Allows users to create AI-generated surveys for their municipality.
 * Features:
 * - Municipality selection (reuses ElectionFilter logic)
 * - AI-powered question generation based on local issues
 * - Preview and edit questions before publishing
 * - QR code and shareable link
 * - Results view
 *
 * Access: Users with at least 1 credit purchased + admins (free, no credit consumed)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getUserCredits } from '../../services/userService';
import {
  findMunicipality,
  generateSurvey,
  listSurveys,
  getSurvey,
  getSurveyResults,
  deleteSurvey,
  listSenateDistricts,
  SenateDistrictListItem
} from '../../services/api';
import {
  MunicipalityResult,
  SurveyTemplate,
  SurveyListItem,
  SurveyResults
} from '../../types';

// Icons
const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-8 h-8">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);

const SurveyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M9 11l3 3L22 4"/>
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
  </svg>
);

const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <polyline points="3,6 5,6 21,6"/>
    <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2v2"/>
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const QRIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <rect x="3" y="3" width="7" height="7"/>
    <rect x="14" y="3" width="7" height="7"/>
    <rect x="3" y="14" width="7" height="7"/>
    <path d="M14 14h3v3h-3z"/>
    <path d="M17 17h4v4h-4z"/>
    <path d="M14 17h3"/>
    <path d="M17 14v3"/>
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
  </svg>
);

const SurveyPage: React.FC = () => {
  const { user, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();

  // State
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [credits, setCredits] = useState<number>(0);
  const [canCreate, setCanCreate] = useState<boolean>(false);

  // Survey creation state
  const [electionType, setElectionType] = useState<string>('komunalni');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<MunicipalityResult[]>([]);
  const [selectedMunicipality, setSelectedMunicipality] = useState<MunicipalityResult | null>(null);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [clientParty, setClientParty] = useState<string>('');
  const [customTopics, setCustomTopics] = useState<string[]>([]);
  const [customTopicInput, setCustomTopicInput] = useState<string>('');

  // Senate districts state (for dropdown when election type is 'senatni')
  const [senateDistricts, setSenateDistricts] = useState<SenateDistrictListItem[]>([]);
  const [senateDistrictsLoading, setSenateDistrictsLoading] = useState<boolean>(false);
  const [selectedDistrictId, setSelectedDistrictId] = useState<string>('');

  // Generation state
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generatedSurvey, setGeneratedSurvey] = useState<SurveyTemplate | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  // Survey list state
  const [mySurveys, setMySurveys] = useState<SurveyListItem[]>([]);
  const [isLoadingSurveys, setIsLoadingSurveys] = useState<boolean>(true);
  const [selectedSurveyResults, setSelectedSurveyResults] = useState<SurveyResults | null>(null);
  const [isLoadingResults, setIsLoadingResults] = useState<boolean>(false);

  // QR code modal state
  const [qrModalSurvey, setQrModalSurvey] = useState<{id: string, name: string, qrUrl: string, publicUrl: string} | null>(null);
  const [isLoadingQR, setIsLoadingQR] = useState<boolean>(false);

  // Load credits and surveys on mount
  useEffect(() => {
    const loadData = async () => {
      if (user?.uid) {
        // Load credits
        const userCredits = await getUserCredits(user.uid);
        setCredits(userCredits);
        // Users can create if they have ever purchased credits (>= 1) or are admin
        setCanCreate(isAdmin || userCredits >= 1);

        // Load existing surveys
        try {
          const response = await listSurveys();
          setMySurveys(response.surveys);
        } catch (err) {
          console.error('Failed to load surveys:', err);
        } finally {
          setIsLoadingSurveys(false);
        }
      }
    };
    loadData();
  }, [user?.uid, isAdmin]);

  // Load senate districts when election type changes to senatni
  useEffect(() => {
    const loadSenateDistricts = async () => {
      if (electionType !== 'senatni') {
        return;
      }

      // Only load if we don't have districts yet
      if (senateDistricts.length > 0) {
        return;
      }

      setSenateDistrictsLoading(true);
      try {
        const districts = await listSenateDistricts();
        setSenateDistricts(districts);
      } catch (error) {
        console.error('Failed to load senate districts:', error);
      } finally {
        setSenateDistrictsLoading(false);
      }
    };

    loadSenateDistricts();
  }, [electionType, senateDistricts.length]);

  // Reset selection when election type changes
  useEffect(() => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedMunicipality(null);
    setSelectedDistrictId('');
    setClientParty('');
  }, [electionType]);

  // Municipality search with debounce
  useEffect(() => {
    const searchMunicipality = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const results = await findMunicipality(searchQuery, 30);
        setSearchResults(results);
      } catch (err) {
        console.error('Municipality search error:', err);
      } finally {
        setIsSearching(false);
      }
    };

    const timer = setTimeout(searchMunicipality, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle municipality selection (for komunalni)
  const handleSelectMunicipality = (municipality: MunicipalityResult) => {
    setSelectedMunicipality(municipality);
    setSearchQuery(municipality.name);
    setSearchResults([]);
  };

  // Handle senate district selection (for senatni)
  const handleSenateDistrictSelect = (districtId: string) => {
    setSelectedDistrictId(districtId);
    const district = senateDistricts.find(d => d.district_id.toString() === districtId);
    if (district) {
      // Create a synthetic MunicipalityResult to reuse existing logic
      setSelectedMunicipality({
        code: districtId, // district_id as code (backend expects 1-81 for senate)
        name: `Obvod ${district.district_id} - ${district.district_name}`,
        district: district.district_name,
        region: '',
        match_score: 100 // Perfect match for direct selection
      });
    } else {
      setSelectedMunicipality(null);
    }
  };

  // Generate survey
  const handleGenerateSurvey = async () => {
    if (!selectedMunicipality || !user?.uid) return;

    setIsGenerating(true);
    setGenerationError(null);

    const previousSurveyCount = mySurveys.length;

    try {
      const response = await generateSurvey({
        municipality_code: selectedMunicipality.code,
        municipality_name: selectedMunicipality.name,
        election_type: electionType,
        client_party: clientParty || undefined,
        custom_topics: customTopics.length > 0 ? customTopics : undefined,
        num_questions: 15
      });

      setGeneratedSurvey(response.survey);

      // Refresh survey list
      const listResponse = await listSurveys();
      setMySurveys(listResponse.surveys);
    } catch (err: any) {
      console.error('Survey generation error:', err);

      // Check if survey was created despite the error (timeout, serialization, etc.)
      try {
        const listResponse = await listSurveys();
        if (listResponse.surveys.length > previousSurveyCount) {
          // Survey was created despite the error
          setMySurveys(listResponse.surveys);
          setGeneratedSurvey(null);
          setGenerationError(null);
          // Show a soft info message instead of error
          alert('Dotazník byl úspěšně vytvořen! (Zobrazení odpovědi trvalo déle než obvykle.)');
        } else {
          setGenerationError(err.message || 'Nepodařilo se vygenerovat dotazník');
        }
      } catch {
        setGenerationError(err.message || 'Nepodařilo se vygenerovat dotazník');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Copy URL to clipboard
  const handleCopyUrl = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  // View results — scroll into view after load
  const resultsRef = React.useRef<HTMLDivElement>(null);

  const handleViewResults = async (surveyId: string) => {
    if (!user?.uid) return;

    setIsLoadingResults(true);
    try {
      const results = await getSurveyResults(surveyId);
      setSelectedSurveyResults(results);
      // Scroll to results after a short delay so DOM renders
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err: any) {
      console.error('Failed to load results:', err);
      alert('Nepodařilo se načíst výsledky: ' + (err.message || 'Neznámá chyba'));
    } finally {
      setIsLoadingResults(false);
    }
  };

  // Delete survey
  const handleDeleteSurvey = async (surveyId: string) => {
    if (!user?.uid) return;
    if (!window.confirm('Opravdu chcete smazat tento dotazník a všechny odpovědi?')) return;

    try {
      await deleteSurvey(surveyId);
      setMySurveys(mySurveys.filter(s => s.id !== surveyId));
      if (selectedSurveyResults?.survey_id === surveyId) {
        setSelectedSurveyResults(null);
      }
    } catch (err: any) {
      console.error('Failed to delete survey:', err);
      alert('Nepodařilo se smazat dotazník: ' + err.message);
    }
  };

  // Show QR code modal
  const handleShowQR = async (surveyId: string, surveyName: string) => {
    setIsLoadingQR(true);
    try {
      const surveyData = await getSurvey(surveyId);
      setQrModalSurvey({
        id: surveyId,
        name: surveyName,
        qrUrl: surveyData.qr_code_url || '',
        publicUrl: surveyData.public_url || ''
      });
    } catch (err: any) {
      console.error('Failed to load QR code:', err);
      alert('Nepodařilo se načíst QR kód');
    } finally {
      setIsLoadingQR(false);
    }
  };

  // Sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  // Get question type label
  const getQuestionTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'likert': 'Škála 1-5',
      'yes_no': 'Ano/Ne',
      'choice': 'Výběr',
      'open': 'Otevřená'
    };
    return labels[type] || type;
  };

  // Get section label
  const getSectionLabel = (section: string): string => {
    const labels: Record<string, string> = {
      'filtering': '1. Filtrační',
      'satisfaction': '2. Spokojenost',
      'priorities': '3. Priority',
      'values': '4. Hodnoty',
      'trust': '5. Důvěra',
      'communication': '6. Komunikace',
      'open_questions': '7. Otevřené',
      'demographics': '8. Demografie',
      'local_issues': 'Místní témata',  // Legacy
      'campaign': 'Kampaň'  // Legacy
    };
    return labels[section] || section;
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50"
        style={{
          background: 'linear-gradient(135deg, var(--phoenix-700) 0%, var(--phoenix-900) 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                style={{
                  background: 'linear-gradient(135deg, var(--phoenix-400) 0%, var(--phoenix-500) 100%)',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
                }}
              >
                <LogoIcon />
              </div>
              <div>
                <h1 className="font-display text-lg font-semibold text-white tracking-tight">
                  Phoenix Analytics
                </h1>
                <p className="text-xs text-white/60">Dotazník pro občany</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              <Link
                to="/filter"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all text-white/80 hover:text-white hover:bg-white/10"
              >
                <span className="flex items-center gap-2">
                  <ChartIcon />
                  Analýza
                </span>
              </Link>
              <Link
                to="/survey"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all text-white"
                style={{ background: 'rgba(255, 255, 255, 0.15)' }}
              >
                <span className="flex items-center gap-2">
                  <SurveyIcon />
                  Dotazník
                </span>
              </Link>
            </nav>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 px-3 py-2 rounded-xl transition-all hover:bg-white/10"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white"
                  style={{ background: 'rgba(255, 255, 255, 0.2)' }}
                >
                  <UserIcon />
                </div>
                <span className="hidden sm:block text-sm text-white/90 max-w-[150px] truncate">
                  {user?.email}
                </span>
              </button>

              {showUserMenu && (
                <div
                  className="absolute right-0 mt-2 w-56 rounded-xl overflow-hidden"
                  style={{
                    background: 'var(--bg-secondary)',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-light)' }}>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      Přihlášen jako
                    </p>
                    <p className="text-sm truncate" style={{ color: 'var(--text-secondary)' }}>
                      {user?.email}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        Kredity:
                      </span>
                      <span
                        className="px-2 py-0.5 rounded-full text-sm font-semibold"
                        style={{
                          background: isAdmin ? 'var(--accent-primary)' : (credits > 0 ? '#10B981' : '#EF4444'),
                          color: 'white'
                        }}
                      >
                        {isAdmin ? '∞' : credits}
                      </span>
                    </div>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-red-50"
                      style={{ color: 'var(--danger-600)' }}
                    >
                      <LogoutIcon />
                      Odhlásit se
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2
            className="font-display text-2xl lg:text-3xl font-semibold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            Dotazník pro občany
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Vytvořte anonymní dotazník pro občany vaší obce. AI vygeneruje relevantní otázky na základě místních témat.
          </p>
        </div>

        {/* Access Check */}
        {!canCreate && (
          <div
            className="rounded-xl p-6 mb-8"
            style={{
              background: 'var(--warning-100)',
              border: '1px solid var(--warning-300)'
            }}
          >
            <div className="flex items-start gap-3">
              <span style={{ color: 'var(--warning-600)' }}><AlertIcon /></span>
              <div>
                <p className="font-medium" style={{ color: 'var(--warning-700)' }}>
                  Přístup k dotazníkům
                </p>
                <p className="text-sm mt-1" style={{ color: 'var(--warning-600)' }}>
                  Pro vytváření dotazníků musíte mít alespoň 1 zakoupený kredit.
                  Dotazníky jsou zdarma a nespotřebovávají kredity.{' '}
                  <Link
                    to="/billing"
                    className="underline font-medium hover:no-underline"
                  >
                    Koupit kredity →
                  </Link>
                </p>
              </div>
            </div>
          </div>
        )}

        {canCreate && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Create Survey */}
            <div>
              <div
                className="rounded-2xl p-6"
                style={{
                  background: 'var(--bg-secondary)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              >
                <h3
                  className="font-display text-lg font-semibold mb-6 flex items-center gap-2"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <span
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: 'var(--phoenix-100)', color: 'var(--phoenix-600)' }}
                  >
                    <SurveyIcon />
                  </span>
                  Vytvořit nový dotazník
                </h3>

                {/* Election Type */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Typ voleb
                  </label>
                  <select
                    value={electionType}
                    onChange={(e) => setElectionType(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500"
                    style={{
                      background: 'var(--bg-primary)',
                      borderColor: 'var(--border-light)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <option value="komunalni">Komunální volby</option>
                    <option value="senatni">Senátní volby</option>
                  </select>
                </div>

                {/* Senate District Dropdown OR Municipality Search */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    {electionType === 'senatni' ? 'Senátní obvod' : 'Obec/Město'}
                  </label>

                  {electionType === 'senatni' ? (
                    /* Senate District Dropdown */
                    <div className="relative">
                      <select
                        value={selectedDistrictId}
                        onChange={(e) => handleSenateDistrictSelect(e.target.value)}
                        disabled={senateDistrictsLoading}
                        className="w-full px-4 py-3 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                        style={{
                          background: 'var(--bg-primary)',
                          borderColor: 'var(--border-light)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        <option value="">Vyberte senátní obvod (1-81)...</option>
                        {senateDistricts.map((d) => (
                          <option key={d.district_id} value={d.district_id}>
                            {d.district_id}. {d.district_name}
                          </option>
                        ))}
                      </select>
                      <span
                        className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                        style={{ color: 'var(--text-tertiary)' }}
                      >
                        {senateDistrictsLoading ? <SpinnerIcon /> : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                            <polyline points="6 9 12 15 18 9"/>
                          </svg>
                        )}
                      </span>
                    </div>
                  ) : (
                    /* Municipality Search Input */
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Začněte psát název obce..."
                        className="w-full px-4 py-3 pl-10 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500"
                        style={{
                          background: 'var(--bg-primary)',
                          borderColor: 'var(--border-light)',
                          color: 'var(--text-primary)'
                        }}
                      />
                      <span
                        className="absolute left-3 top-1/2 -translate-y-1/2"
                        style={{ color: 'var(--text-tertiary)' }}
                      >
                        {isSearching ? <SpinnerIcon /> : <SearchIcon />}
                      </span>

                      {/* Search Results Dropdown */}
                      {searchResults.length > 0 && (
                        <div
                          className="absolute top-full left-0 right-0 mt-1 rounded-xl overflow-hidden z-10"
                          style={{
                            background: 'var(--bg-secondary)',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                            border: '1px solid var(--border-light)'
                          }}
                        >
                          {searchResults.map((result) => (
                            <button
                              key={result.code}
                              onClick={() => handleSelectMunicipality(result)}
                              className="w-full px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                            >
                              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                                {result.name}
                              </p>
                              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                {result.district}, {result.region}
                              </p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {selectedMunicipality && (
                    <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                      {electionType === 'senatni' ? 'Vybraný obvod' : 'Vybraná obec'}: <strong>{selectedMunicipality.name}</strong> (kód: {selectedMunicipality.code})
                    </p>
                  )}
                </div>

                {/* Client Party (Optional) */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Vaše strana/hnutí (volitelné)
                  </label>
                  <input
                    type="text"
                    value={clientParty}
                    onChange={(e) => setClientParty(e.target.value)}
                    placeholder="např. ODS, ANO, Piráti..."
                    className="w-full px-4 py-3 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500"
                    style={{
                      background: 'var(--bg-primary)',
                      borderColor: 'var(--border-light)',
                      color: 'var(--text-primary)'
                    }}
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                    Otázky budou neutrální, ale AI zohlední relevantní témata pro vaši stranu.
                  </p>
                </div>

                {/* Custom Topics */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Vlastní témata (volitelné)
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={customTopicInput}
                      onChange={(e) => setCustomTopicInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && customTopicInput.trim()) {
                          e.preventDefault();
                          setCustomTopics(prev => [...prev, customTopicInput.trim()]);
                          setCustomTopicInput('');
                        }
                      }}
                      placeholder="např. parkování u nemocnice, cyklostezky..."
                      className="flex-1 px-4 py-3 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500"
                      style={{
                        background: 'var(--bg-primary)',
                        borderColor: 'var(--border-light)',
                        color: 'var(--text-primary)'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (customTopicInput.trim()) {
                          setCustomTopics(prev => [...prev, customTopicInput.trim()]);
                          setCustomTopicInput('');
                        }
                      }}
                      disabled={!customTopicInput.trim()}
                      className="px-4 py-3 rounded-xl font-medium transition-all disabled:opacity-50"
                      style={{
                        background: 'var(--phoenix-100)',
                        color: 'var(--phoenix-700)'
                      }}
                    >
                      +
                    </button>
                  </div>
                  {customTopics.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {customTopics.map((topic, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm"
                          style={{
                            background: 'var(--phoenix-100)',
                            color: 'var(--phoenix-700)'
                          }}
                        >
                          {topic}
                          <button
                            type="button"
                            onClick={() => setCustomTopics(prev => prev.filter((_, idx) => idx !== i))}
                            className="ml-1 hover:opacity-70"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                    Zadejte konkrétní témata, která vás zajímají. AI je zahrne do dotazníku nad rámec standardních otázek. Stiskněte Enter pro přidání.
                  </p>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerateSurvey}
                  disabled={!selectedMunicipality || isGenerating}
                  className="w-full py-4 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, var(--phoenix-500) 0%, var(--phoenix-600) 100%)',
                    boxShadow: '0 4px 14px rgba(0, 70, 140, 0.3)'
                  }}
                >
                  {isGenerating ? (
                    <span className="flex items-center justify-center gap-2">
                      <SpinnerIcon />
                      Generuji otázky pomocí AI...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <SurveyIcon />
                      Vygenerovat dotazník
                    </span>
                  )}
                </button>

                {/* Error */}
                {generationError && (
                  <div
                    className="mt-4 rounded-xl p-4 flex items-start gap-3"
                    style={{
                      background: 'var(--danger-100)',
                      border: '1px solid var(--danger-300)'
                    }}
                  >
                    <span style={{ color: 'var(--danger-600)' }}><AlertIcon /></span>
                    <p className="text-sm" style={{ color: 'var(--danger-600)' }}>
                      {generationError}
                    </p>
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div
                className="rounded-2xl p-6 mt-6"
                style={{
                  background: 'var(--bg-secondary)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              >
                <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Jak to funguje?
                </h4>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    AI vyhledá aktuální problémy a témata ve vaší obci
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    Vygeneruje 15 relevantních otázek ve 4 sekcích
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    Získáte QR kód a odkaz pro sdílení s občany
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    Výsledky se automaticky zahrnují do analýzy strategie
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    Zdarma - dotazníky nespotřebovávají kredity
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Column - Generated Survey or My Surveys */}
            <div>
              {/* Generated Survey Preview */}
              {generatedSurvey && (
                <div
                  className="rounded-2xl p-6 mb-6"
                  style={{
                    background: 'var(--bg-secondary)',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    border: '2px solid var(--success-300)'
                  }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3
                        className="font-display text-lg font-semibold flex items-center gap-2"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        <span style={{ color: 'var(--success-500)' }}><CheckIcon /></span>
                        Dotazník vytvořen!
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {generatedSurvey.title}
                      </p>
                    </div>
                  </div>

                  {/* Public URL */}
                  <div
                    className="p-4 rounded-xl mb-4"
                    style={{ background: 'var(--bg-tertiary)' }}
                  >
                    <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      Veřejný odkaz pro občany:
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={generatedSurvey.public_url || ''}
                        className="flex-1 px-3 py-2 rounded-lg text-sm border"
                        style={{
                          background: 'var(--bg-primary)',
                          borderColor: 'var(--border-light)',
                          color: 'var(--text-primary)'
                        }}
                      />
                      <button
                        onClick={() => handleCopyUrl(generatedSurvey.public_url || '')}
                        className="px-3 py-2 rounded-lg transition-all hover:bg-gray-100 dark:hover:bg-gray-700"
                        style={{ color: copied ? 'var(--success-500)' : 'var(--phoenix-600)' }}
                      >
                        {copied ? <CheckIcon /> : <CopyIcon />}
                      </button>
                    </div>
                  </div>

                  {/* QR Code */}
                  {generatedSurvey.qr_code_url && (
                    <div className="flex items-center gap-4 mb-4">
                      <img
                        src={generatedSurvey.qr_code_url}
                        alt="QR kód dotazníku"
                        className="w-24 h-24 rounded-lg"
                        style={{ border: '2px solid var(--border-light)' }}
                      />
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          QR kód
                        </p>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          Naskenováním se otevře dotazník na telefonu.
                        </p>
                        <a
                          href={generatedSurvey.qr_code_url}
                          download={`qr_dotaznik_${generatedSurvey.id}.png`}
                          className="text-xs font-medium mt-1 inline-block"
                          style={{ color: 'var(--phoenix-600)' }}
                        >
                          Stáhnout QR kód
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Questions Preview */}
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      {generatedSurvey.questions.length} otázek ve 4 sekcích:
                    </p>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {generatedSurvey.questions.map((q, i) => (
                        <div
                          key={q.id}
                          className="p-3 rounded-lg text-sm"
                          style={{ background: 'var(--bg-tertiary)' }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p style={{ color: 'var(--text-primary)' }}>
                              {i + 1}. {q.text}
                            </p>
                            <span
                              className="px-2 py-0.5 rounded text-xs flex-shrink-0"
                              style={{ background: 'var(--phoenix-100)', color: 'var(--phoenix-700)' }}
                            >
                              {getQuestionTypeLabel(q.type)}
                            </span>
                          </div>
                          <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                            {getSectionLabel(q.section)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* My Surveys List */}
              <div
                className="rounded-2xl p-6"
                style={{
                  background: 'var(--bg-secondary)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              >
                <h3
                  className="font-display text-lg font-semibold mb-4"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Moje dotazníky
                </h3>

                {isLoadingSurveys ? (
                  <div className="flex items-center justify-center py-8">
                    <SpinnerIcon />
                    <span className="ml-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      Načítám...
                    </span>
                  </div>
                ) : mySurveys.length === 0 ? (
                  <p className="text-sm py-8 text-center" style={{ color: 'var(--text-tertiary)' }}>
                    Zatím nemáte žádné dotazníky.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {mySurveys.map((survey) => (
                      <div
                        key={survey.id}
                        className="p-4 rounded-xl transition-all hover:bg-gray-50 dark:hover:bg-gray-800"
                        style={{ border: '1px solid var(--border-light)' }}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                              {survey.municipality_name}
                            </p>
                            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                              {survey.election_type === 'komunalni' ? 'Komunální volby' : 'Senátní volby'}
                              {' • '}
                              {new Date(survey.created_at).toLocaleDateString('cs-CZ')}
                            </p>
                          </div>
                          <span
                            className="px-2 py-1 rounded-full text-xs font-medium"
                            style={{
                              background: survey.is_active ? 'var(--success-100)' : 'var(--warning-100)',
                              color: survey.is_active ? 'var(--success-700)' : 'var(--warning-700)'
                            }}
                          >
                            {survey.response_count} odpovědí
                          </span>
                        </div>

                        <div className="flex items-center gap-2 mt-3">
                          {survey.public_url && (
                            <>
                              <button
                                onClick={() => handleCopyUrl(survey.public_url!)}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-gray-100 dark:hover:bg-gray-700"
                                style={{ color: 'var(--phoenix-600)' }}
                              >
                                <span className="flex items-center gap-1">
                                  <CopyIcon /> Odkaz
                                </span>
                              </button>
                              <button
                                onClick={() => handleShowQR(survey.id, survey.municipality_name)}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-gray-100 dark:hover:bg-gray-700"
                                style={{ color: 'var(--phoenix-600)' }}
                              >
                                <span className="flex items-center gap-1">
                                  <QRIcon /> QR
                                </span>
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => navigate(`/survey/${survey.id}/edit`)}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-gray-100 dark:hover:bg-gray-700"
                            style={{ color: 'var(--phoenix-600)' }}
                          >
                            <span className="flex items-center gap-1">
                              <EditIcon /> Upravit
                            </span>
                          </button>
                          <button
                            onClick={() => handleViewResults(survey.id)}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-gray-100 dark:hover:bg-gray-700"
                            style={{ color: 'var(--phoenix-600)' }}
                          >
                            <span className="flex items-center gap-1">
                              <ChartIcon /> Výsledky
                            </span>
                          </button>
                          <button
                            onClick={() => handleDeleteSurvey(survey.id)}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-red-50"
                            style={{ color: 'var(--danger-500)' }}
                          >
                            <span className="flex items-center gap-1">
                              <TrashIcon /> Smazat
                            </span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Results View */}
              {selectedSurveyResults && (
                <div
                  ref={resultsRef}
                  className="rounded-2xl p-6 mt-6"
                  style={{
                    background: 'var(--bg-secondary)',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    border: '2px solid var(--phoenix-200)'
                  }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3
                        className="font-display text-lg font-semibold"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        Výsledky: {selectedSurveyResults.municipality_name}
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {selectedSurveyResults.total_responses} odpovědí
                        {selectedSurveyResults.first_response_at && (
                          <> (od {new Date(selectedSurveyResults.first_response_at).toLocaleDateString('cs-CZ')})</>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedSurveyResults(null)}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      ✕
                    </button>
                  </div>

                  {selectedSurveyResults.total_responses === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                        Zatím žádné odpovědi. Sdílejte odkaz nebo QR kód s občany.
                      </p>
                    </div>
                  ) : isLoadingResults ? (
                    <div className="flex items-center justify-center py-8">
                      <SpinnerIcon />
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-[600px] overflow-y-auto">
                      {selectedSurveyResults.question_results.map((qr) => (
                        <div
                          key={qr.question_id}
                          className="p-4 rounded-xl"
                          style={{ background: 'var(--bg-tertiary)' }}
                        >
                          <p className="font-medium text-sm mb-2" style={{ color: 'var(--text-primary)' }}>
                            {qr.question_text}
                          </p>
                          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {qr.question_type === 'likert' && qr.results.average !== undefined && (
                              <div>
                                <p className="mb-1">Průměr: <strong>{Number(qr.results.average).toFixed(1)}</strong> / 5</p>
                                <div className="flex gap-1 items-end h-8">
                                  {[1, 2, 3, 4, 5].map(val => {
                                    const count = (qr.results[String(val)] as number) || 0;
                                    const maxCount = Math.max(...[1,2,3,4,5].map(v => (qr.results[String(v)] as number) || 0), 1);
                                    const height = (count / maxCount) * 100;
                                    return (
                                      <div key={val} className="flex-1 flex flex-col items-center gap-0.5">
                                        <div
                                          className="w-full rounded-sm"
                                          style={{
                                            height: `${Math.max(height, 4)}%`,
                                            background: val <= 2 ? 'var(--danger-400)' : val === 3 ? 'var(--warning-400)' : 'var(--success-400)',
                                            minHeight: '2px'
                                          }}
                                        />
                                        <span className="text-[10px]">{val}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {qr.question_type === 'yes_no' && (() => {
                              const anoCount = (qr.results.ano as number) || 0;
                              const neCount = (qr.results.ne as number) || 0;
                              const total = anoCount + neCount;
                              const anoPct = total > 0 ? Math.round((anoCount / total) * 100) : 0;
                              const nePct = total > 0 ? Math.round((neCount / total) * 100) : 0;
                              return (
                                <div className="flex gap-4">
                                  <span>Ano: <strong>{anoPct}%</strong> ({anoCount})</span>
                                  <span>Ne: <strong>{nePct}%</strong> ({neCount})</span>
                                </div>
                              );
                            })()}
                            {qr.question_type === 'choice' && (
                              <div className="space-y-1">
                                {Object.entries(qr.results)
                                  .sort(([,a], [,b]) => (b as number) - (a as number))
                                  .map(([option, count]) => {
                                    const maxCount = Math.max(...Object.values(qr.results).map(v => v as number), 1);
                                    const widthPct = ((count as number) / maxCount) * 100;
                                    return (
                                      <div key={option}>
                                        <div className="flex justify-between mb-0.5">
                                          <span className="truncate mr-2">{option}</span>
                                          <strong className="flex-shrink-0">{count as number}x</strong>
                                        </div>
                                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                          <div
                                            className="h-full rounded-full"
                                            style={{ width: `${widthPct}%`, background: 'var(--phoenix-500)' }}
                                          />
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                            {qr.question_type === 'open' && qr.results.responses && (
                              <div className="space-y-2">
                                <p className="font-medium" style={{ color: 'var(--text-tertiary)' }}>
                                  {(qr.results.responses as string[]).length} odpovědí:
                                </p>
                                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                  {(qr.results.responses as string[]).map((resp, i) => (
                                    <div
                                      key={i}
                                      className="p-2 rounded-lg text-xs italic"
                                      style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                                    >
                                      "{resp}"
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <p className="mt-1 opacity-70">{qr.total_responses} odpovědí</p>
                          </div>
                        </div>
                      ))}

                      {selectedSurveyResults.top_priorities.length > 0 && (
                        <div
                          className="p-4 rounded-xl"
                          style={{ background: 'var(--phoenix-50)' }}
                        >
                          <p className="font-medium text-sm mb-2" style={{ color: 'var(--phoenix-700)' }}>
                            Top priority občanů:
                          </p>
                          <ol className="list-decimal list-inside text-sm" style={{ color: 'var(--phoenix-600)' }}>
                            {selectedSurveyResults.top_priorities.map((p, i) => (
                              <li key={i}>{p}</li>
                            ))}
                          </ol>
                        </div>
                      )}

                      {selectedSurveyResults.key_concerns && selectedSurveyResults.key_concerns.length > 0 && (
                        <div
                          className="p-4 rounded-xl"
                          style={{ background: 'var(--warning-50, #fffbeb)' }}
                        >
                          <p className="font-medium text-sm mb-2" style={{ color: 'var(--warning-700, #b45309)' }}>
                            Hlavní obavy občanů:
                          </p>
                          <ul className="list-disc list-inside text-sm" style={{ color: 'var(--warning-600, #d97706)' }}>
                            {selectedSurveyResults.key_concerns.map((c, i) => (
                              <li key={i}>{c}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer
        className="mt-12 py-8"
        style={{
          background: 'linear-gradient(135deg, var(--phoenix-800) 0%, var(--phoenix-900) 100%)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                style={{ background: 'rgba(255, 255, 255, 0.1)' }}
              >
                <LogoIcon />
              </div>
              <span className="text-white/80 text-sm">
                Phoenix Electoral Analytics v6.8
              </span>
            </div>
            <div className="text-center md:text-right">
              <p className="text-white/60 text-sm">
                Data: ČSÚ (Český statistický úřad)
              </p>
              <p className="text-white/40 text-xs mt-1">
                © 2025 Milan Pecka
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Click outside to close menu */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserMenu(false)}
        />
      )}

      {/* QR Code Modal */}
      {(qrModalSurvey || isLoadingQR) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !isLoadingQR && setQrModalSurvey(null)}
          />

          {/* Modal */}
          <div
            className="relative rounded-2xl p-6 max-w-sm w-full"
            style={{ background: 'var(--bg-secondary)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
          >
            {isLoadingQR ? (
              <div className="flex items-center justify-center py-12">
                <SpinnerIcon />
                <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Načítám QR kód...</span>
              </div>
            ) : qrModalSurvey && (
              <>
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                      QR kód pro sdílení
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {qrModalSurvey.name}
                    </p>
                  </div>
                  <button
                    onClick={() => setQrModalSurvey(null)}
                    className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    style={{ color: 'var(--text-tertiary)' }}
                  >
                    <CloseIcon />
                  </button>
                </div>

                {/* QR Code */}
                {qrModalSurvey.qrUrl ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={qrModalSurvey.qrUrl}
                      alt="QR kód dotazníku"
                      className="w-48 h-48 rounded-xl mb-4"
                      style={{ border: '2px solid var(--border-light)' }}
                    />
                    <p className="text-xs text-center mb-4" style={{ color: 'var(--text-tertiary)' }}>
                      Naskenujte telefonem pro otevření dotazníku
                    </p>

                    {/* Actions */}
                    <div className="flex gap-2 w-full">
                      <a
                        href={qrModalSurvey.qrUrl}
                        download={`qr_dotaznik_${qrModalSurvey.id}.png`}
                        className="flex-1 py-2 px-4 rounded-lg text-center text-sm font-medium transition-all"
                        style={{
                          background: 'var(--phoenix-500)',
                          color: 'white'
                        }}
                      >
                        Stáhnout PNG
                      </a>
                      <button
                        onClick={() => {
                          handleCopyUrl(qrModalSurvey.publicUrl);
                          setQrModalSurvey(null);
                        }}
                        className="flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all"
                        style={{
                          background: 'var(--bg-tertiary)',
                          color: 'var(--text-primary)',
                          border: '1px solid var(--border-light)'
                        }}
                      >
                        Kopírovat odkaz
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p style={{ color: 'var(--text-secondary)' }}>QR kód není k dispozici</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyPage;
